// src/components/trackinginfo/pages/TrackingInfoPage.jsx
// ============================================================================
// BLOCK TI.PAGE.0 — Tracking Info Page (Route-Level Component)
// ============================================================================

import React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import MonthSelector from '../../MonthSelector.jsx';
import YearSelector from '../../YearSelector.jsx';
import fetchTrackingRows from '../api/trackingInfoApi.js';
import TrackingInfoRepSelector from '../components/TrackingInfoRepSelector.jsx';
import TrackingInfoTable from '../components/TrackingInfoTable.jsx';

function toSearchableDateTokens(value) {
  if (!value) {
    return [];
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return [];
  }

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year2 = String(date.getFullYear()).slice(-2);
  const year4 = String(date.getFullYear());

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');

  return [
    `${month}/${day}/${year2}`,
    `${mm}/${dd}/${year2}`,
    `${month}/${day}/${year4}`,
    `${mm}/${dd}/${year4}`,
    `${year4}-${mm}-${dd}`,
  ];
}

function normalizeRowToSearchText(r) {
  const dateTokens = [r?.ShipDate, r?.DeliveryDate].flatMap(toSearchableDateTokens);

  // Keep this strictly aligned with columns rendered in TrackingInfoTable
  const parts = [
    r?.ShipDate,
    r?.Company,
    r?.CustomerRef,
    r?.MstrTrackingNo,
    r?.TrackingNo,
    r?.DeliveryDate,
    r?.City,
    r?.State,
    r?.ZipCode,
    r?.OrderNo,
    r?.ServiceType,
    r?.ServiceTypeDesc,
    r?.PCharge,
    ...dateTokens,
  ];

  return parts
    .map((v) => (v === null || v === undefined ? '' : String(v)))
    .join(' ')
    .toLowerCase();
}

const TRACKING_BATCH_SIZE = 5000;

function TrackingInfoPage() {
  const [year, setYear] = React.useState(() => new Date().getFullYear());
  const [month, setMonth] = React.useState(() => new Date().getMonth() + 1);

  // repNo is SalesPersonNumber (string). '' => All reps
  const [repNo, setRepNo] = React.useState('');

  // global search (client-side)
  const [searchText, setSearchText] = React.useState('');

  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await fetchTrackingRows({
        year,
        month,
        rep: repNo || null, // null => show ALL rows for date range
        offset: 0,
        limit: TRACKING_BATCH_SIZE,
      });

      const initialRows = Array.isArray(result?.rows) ? result.rows : [];
      setRows(initialRows);
      setTotalRows(Number.isFinite(result?.total) ? Number(result.total) : initialRows.length);
    } catch (e) {
      setRows([]);
      setTotalRows(0);
      setError(e?.message ?? 'Failed to load tracking info');
    } finally {
      setLoading(false);
    }
  }, [year, month, repNo]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filteredRows = React.useMemo(() => {
    const q = String(searchText || '')
      .trim()
      .toLowerCase();
    if (!q) {
      return rows;
    }
    return (rows || []).filter((r) => normalizeRowToSearchText(r).includes(q));
  }, [rows, searchText]);

  const canFetchMore = rows.length < totalRows;

  const handleFetchMore = React.useCallback(async () => {
    if (loadingMore || loading || !canFetchMore) {
      return;
    }

    setLoadingMore(true);
    setError('');

    try {
      const result = await fetchTrackingRows({
        year,
        month,
        rep: repNo || null,
        offset: rows.length,
        limit: TRACKING_BATCH_SIZE,
      });

      const nextRows = Array.isArray(result?.rows) ? result.rows : [];

      setRows((prev) => {
        if (!nextRows.length) {
          return prev;
        }
        return [...prev, ...nextRows];
      });

      setTotalRows((prevTotal) => {
        if (Number.isFinite(result?.total)) {
          return Number(result.total);
        }
        return prevTotal;
      });
    } catch (e) {
      setError(e?.message ?? 'Failed to fetch additional tracking rows');
    } finally {
      setLoadingMore(false);
    }
  }, [canFetchMore, loading, loadingMore, month, repNo, rows.length, year]);

  return (
    <Box>
      <Stack spacing={2}>
        <Typography variant="h5">Shipping Tracking</Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <MonthSelector selectedMonth={month} onChange={setMonth} />
          <YearSelector selectedYear={year} onChange={setYear} />
          <TrackingInfoRepSelector selectedRepNo={repNo} onChange={setRepNo} />

          {/* ================================================================= */}
          {/* BLOCK TI.PAGE.SEARCH.0 — Global client-side search */}
          {/* ================================================================= */}
          <TextField
            size="small"
            label="Search"
            placeholder="Tracking, PO, SO, Company, City, Date (MM/DD/YY)…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 260 }}
          />
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {!loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              onClick={handleFetchMore}
              disabled={loadingMore || !canFetchMore}
            >
              {loadingMore ? 'Fetching…' : 'Fetch Additional Records'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Loaded {rows.length.toLocaleString()} of {totalRows.toLocaleString()} rows
            </Typography>
          </Stack>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TrackingInfoTable rows={filteredRows} />
        )}
      </Stack>
    </Box>
  );
}

export default React.memo(TrackingInfoPage);
